import React, { useCallback, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import type { groupTableBody_nativeIssueTable_groupResults$key } from '@atlassian/jira-relay/src/__generated__/groupTableBody_nativeIssueTable_groupResults.graphql.ts';
import type { groupTableBody_nativeIssueTable_project$key } from '@atlassian/jira-relay/src/__generated__/groupTableBody_nativeIssueTable_project.graphql.ts';
import { getFlatListKey } from '@atlassian/jira-issue-table-hierarchy/src/common/utils/get-flat-list-key/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useHierarchyActions } from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import { ChildRows } from '@atlassian/jira-issue-table-hierarchy/src/ui/child-rows/index.tsx';
import { ChildRowsLoader } from '@atlassian/jira-issue-table-hierarchy/src/ui/child-rows-loader/index.tsx';
import type {
	RenderChildRows,
	RenderRows,
	RenderCreateIssueController,
	RenderDraggableWrapperProps,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/types.tsx';
import {
	FirstGroupRow,
	GroupRow,
} from '@atlassian/jira-issue-table-grouping/src/ui/group-row/index.tsx';
import type { groupTableBody_nativeIssueTable_rankField$key } from '@atlassian/jira-relay/src/__generated__/groupTableBody_nativeIssueTable_rankField.graphql.ts';
import { Rows } from '../../rows/index.tsx';
import type { Column, TableInjectedCellProps } from '../../../common/types.tsx';
import {
	useIsVirtualizationEnabled,
	useIsReparentingEnabled,
	useSortDirection,
	useSortField,
	useJql,
	useIsFixedTableLayoutEnabled,
	useIsIssueRankDescending,
	useIsHideDoneItemsEnabled,
	useIsIssueCreateEnabled,
	useIsTimelineEnabled,
	useIsGridNavigationEnabled,
} from '../../../controllers/features/selectors.tsx';
import { RowContextProvider } from '../../rows/row-context/index.tsx';
import { VirtualizedRows } from '../../rows/virtualized-rows-hierarchy/index.tsx';
import {
	ANALYTICS_SOURCE,
	GROUPED_ITEMS_EXPERIENCE,
	GROUPED_LOAD_MORE_EXPERIENCE,
	MAX_COLUMNS,
	ACTION_COLUMNS,
	CHILD_QUERY_PAGE_SIZE,
} from '../../../common/constants.tsx';

import { TableCell } from '../../../common/ui/table-cell/index.tsx';
import { useScrollStateSelector } from '../../../controllers/scroll-state/index.tsx';
import { CreateIssueController } from '../../../services/issue-create-mutation/index.tsx';
import { DraggableRowsContainer } from '../../../controllers/draggable-rows/context.tsx';

export type Props = {
	groupResults: groupTableBody_nativeIssueTable_groupResults$key;
	columns: Column[];
	loading: boolean;
	tableInjectedProps: TableInjectedCellProps;
	project: groupTableBody_nativeIssueTable_project$key | null;
	rankField: groupTableBody_nativeIssueTable_rankField$key | null;
};

export const GroupTableBody = ({
	groupResults,
	project,
	columns,
	rankField,
	loading,
	...tableBodyProps
}: Props) => {
	const groupsData = useFragment<groupTableBody_nativeIssueTable_groupResults$key>(
		graphql`
			fragment groupTableBody_nativeIssueTable_groupResults on JiraSpreadsheetGroupConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean", defaultValue: true }
				isDensityFull: { type: "Boolean", defaultValue: false }
				isTimelineEnabled: { type: "Boolean!" }
				isPaginating: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
			) {
				__id
				firstGroup @skip(if: $isPaginating) {
					id
					issues(fieldSetsInput: $fieldSetsInput, scope: $fieldSetsScope, first: 50)
						@connection(key: "firstGroupSection_nativeIssueTable__issues") {
						# eslint-disable-next-line @atlassian/relay/unused-fields
						edges {
							__typename
						}
					}
					...groupRow_issueTableGrouping_FirstGroupRow_firstGroupRow
						@arguments(
							isInlineEditingEnabled: $isInlineEditingEnabled
							isTimelineEnabled: $isTimelineEnabled
							isDensityFull: $isDensityFull
							projectKeys: $projectKeys
							projectKey: $projectKey
						)
				}
				edges {
					node {
						id
						...groupRow_issueTableGrouping_groupRow
					}
				}
			}
		`,
		groupResults,
	);

	const projectData = useFragment(
		graphql`
			fragment groupTableBody_nativeIssueTable_project on JiraProject {
				key
			}
		`,
		project,
	);

	const rankFieldData = useFragment(
		graphql`
			fragment groupTableBody_nativeIssueTable_rankField on JiraJqlFieldWithAliases {
				fieldId
				jqlTerm
				...groupRow_issueTableGrouping_FirstGroupRow_rankField
			}
		`,
		rankField,
	);

	const groupConnectionId = groupsData.__id;
	const { populateConnection } = useHierarchyActions();
	const populateConnectionArgs = useMemo(
		() => ({
			parentItemId: 'root',
			itemsConnectionId: groupConnectionId,
			parentConnectionId: groupConnectionId,
			list:
				groupsData?.edges
					// groupId for each group is the same as itemId
					?.map((edge) => edge?.node && { itemId: edge.node.id, key: null, groupId: edge.node.id })
					.filter(Boolean) ?? [],
		}),
		[groupConnectionId, groupsData.edges],
	);
	const prevPopulateConnectionArgs = usePrevious(populateConnectionArgs);

	if (!isEqual(populateConnectionArgs, prevPopulateConnectionArgs)) {
		populateConnection(populateConnectionArgs);
	}

	/**
	 * Passing those values down to ChildRowsLoader as props to avoid direct imports
	 */
	const jql = useJql();
	const sortField = useSortField();
	const sortDirection = useSortDirection();
	const isReparentingEnabled = useIsReparentingEnabled();
	const isTimelineEnabled = useIsTimelineEnabled();
	const isHideDoneItemsEnabled = useIsHideDoneItemsEnabled();
	const isFixedTableLayoutEnabled = useIsFixedTableLayoutEnabled();
	const width = useScrollStateSelector((scrollState) => scrollState.width);
	const isIssueRankDescending = useIsIssueRankDescending();
	const isGridNavigationEnabled = useIsGridNavigationEnabled();

	let isIssueCreateEnabled: boolean | undefined;
	if (fg('jira_list_check_issue_create_permissions')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isIssueCreateEnabled = useIsIssueCreateEnabled();
	}
	const renderDraggableWrapper = useCallback((props: RenderDraggableWrapperProps) => {
		return (
			<DraggableRowsContainer
				issueConnectionId={props.issueConnectionId}
				instanceId={props.instanceId}
			>
				{props.children}
			</DraggableRowsContainer>
		);
	}, []);

	const renderChildRows: RenderChildRows = useCallback(
		(props) => (
			<ChildRows
				{...props}
				projectKey={projectData?.key}
				isGroupingSupported
				childItemsExperience={GROUPED_ITEMS_EXPERIENCE}
				childItemsLoadMoreExperience={GROUPED_LOAD_MORE_EXPERIENCE}
				analyticsSource={ANALYTICS_SOURCE}
				renderChildRowsLoader={(childRowsLoaderProps) => (
					<ChildRowsLoader
						{...childRowsLoaderProps}
						jql={jql}
						width={width}
						sortField={sortField}
						sortDirection={sortDirection}
						columns={columns}
						amountOfColumns={MAX_COLUMNS}
						actionColumns={ACTION_COLUMNS}
						pageSize={CHILD_QUERY_PAGE_SIZE}
						isReparentingEnabled={isReparentingEnabled}
						isTimelineEnabled={isTimelineEnabled}
						isIssueRankDescending={isIssueRankDescending}
						isHideDoneItemsEnabled={isHideDoneItemsEnabled}
						isFixedTableLayoutEnabled={isFixedTableLayoutEnabled}
						rankFieldId={rankFieldData?.fieldId ?? null}
						renderRows={(rowProps) => <Rows {...rowProps} />}
						renderTableCell={(tableCellProps) => <TableCell {...tableCellProps} />}
						renderCreateIssueController={(args) => (
							<CreateIssueController
								{...args}
								groupContextFieldInput={props.groupContextField}
								jql={jql}
								rankJqlTerm={rankFieldData?.jqlTerm ?? null}
							/>
						)}
						{...(fg('jsc_list_reranking_gate_technical') && {
							renderDraggableWrapper,
						})}
					/>
				)}
			/>
		),
		[
			projectData?.key,
			jql,
			width,
			sortField,
			sortDirection,
			columns,
			isReparentingEnabled,
			isTimelineEnabled,
			isIssueRankDescending,
			isHideDoneItemsEnabled,
			isFixedTableLayoutEnabled,
			rankFieldData?.fieldId,
			rankFieldData?.jqlTerm,
			renderDraggableWrapper,
		],
	);

	const renderRows: RenderRows = useCallback((rowProps) => <Rows {...rowProps} />, []);
	const renderCreateIssueController: RenderCreateIssueController = useCallback(
		(args) => <CreateIssueController {...args} jql={jql} />,
		[jql],
	);

	const numFirstGroupIssues = groupsData?.firstGroup?.issues?.edges?.length;
	const isFirstGroupEmpty =
		(isNil(numFirstGroupIssues) || numFirstGroupIssues === 0) &&
		fg('jira_list_skip_first_group_if_no_issues');

	const renderGroups = useCallback(
		(isItemVirtual?: (itemId: string) => boolean) =>
			groupsData?.edges?.map(
				(group, index) =>
					group?.node &&
					// We need to render the first group separately because it requires initialization during the table rendering process.
					// Additionally, it bypasses the ChildRowLoader and directly uses ChildRowsPagination, as the data is already available in data.firstGroup.
					(groupsData?.firstGroup &&
					group.node.id === groupsData.firstGroup.id &&
					!isFirstGroupEmpty ? (
						<RowContextProvider
							key={groupsData.firstGroup.id}
							groupId={groupsData.firstGroup.id}
							groupConnectionId={fg('jsc_list_hide_done_items') ? groupConnectionId : undefined}
							{...tableBodyProps}
							columns={columns}
						>
							<FirstGroupRow
								firstGroupRow={groupsData.firstGroup}
								rankField={rankFieldData ?? null}
								groupConnectionId={groupConnectionId}
								loading={loading}
								isVirtual={
									isItemVirtual?.(
										getFlatListKey({
											itemId: groupsData.firstGroup.id,
											groupId: groupsData.firstGroup.id,
										}),
									) ?? true
								}
								isGroupAtTop={index === 0}
								projectKey={projectData?.key}
								isHideDoneItemsEnabled={isHideDoneItemsEnabled}
								isIssueRankDescending={isIssueRankDescending}
								isTimelineEnabled={isTimelineEnabled}
								columns={columns}
								width={width}
								amountOfColumns={MAX_COLUMNS}
								actionColumns={ACTION_COLUMNS}
								pageSize={CHILD_QUERY_PAGE_SIZE}
								childItemsExperience={GROUPED_ITEMS_EXPERIENCE}
								childItemsLoadMoreExperience={GROUPED_LOAD_MORE_EXPERIENCE}
								analyticsSource={ANALYTICS_SOURCE}
								renderRows={renderRows}
								renderCreateIssueController={renderCreateIssueController}
								isIssueCreateEnabled={isIssueCreateEnabled}
								isGridNavigationEnabled={isGridNavigationEnabled}
								{...(fg('jsc_list_reranking_gate_technical') && {
									renderDraggableWrapper,
								})}
							/>
						</RowContextProvider>
					) : (
						<RowContextProvider
							key={group.node.id}
							groupId={group.node.id}
							groupConnectionId={fg('jsc_list_hide_done_items') ? groupConnectionId : undefined}
							{...tableBodyProps}
							columns={columns}
							renderChildRows={renderChildRows}
						>
							<GroupRow
								groupRow={group.node}
								groupConnectionId={groupConnectionId}
								isVirtual={
									isItemVirtual?.(
										getFlatListKey({ itemId: group.node.id, groupId: group.node.id }),
									) ?? true
								}
								isGroupAtTop={index === 0}
								projectKey={projectData?.key}
								columns={columns}
								renderChildRows={renderChildRows}
								isIssueCreateEnabled={isIssueCreateEnabled}
								isGridNavigationEnabled={isGridNavigationEnabled}
							/>
						</RowContextProvider>
					)),
			),
		[
			groupsData?.edges,
			groupsData.firstGroup,
			isFirstGroupEmpty,
			groupConnectionId,
			tableBodyProps,
			columns,
			rankFieldData,
			loading,
			projectData?.key,
			isHideDoneItemsEnabled,
			isGridNavigationEnabled,
			isIssueRankDescending,
			isTimelineEnabled,
			width,
			renderRows,
			renderCreateIssueController,
			isIssueCreateEnabled,
			renderDraggableWrapper,
			renderChildRows,
		],
	);

	const isVirtualizationEnabled = useIsVirtualizationEnabled();

	return (
		<Box as="tbody" xcss={tableBodyStyles}>
			{isVirtualizationEnabled ? (
				<VirtualizedRows hasSpacers columnsCount={columns.length}>
					{renderGroups}
				</VirtualizedRows>
			) : (
				renderGroups()
			)}
		</Box>
	);
};

const tableBodyStyles = xcss({
	borderBottom: 'none',
	overflowX: 'hidden',
});
